import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import {
  Vacation,
  VacationListResponse,
  VacationRequest,
  VacationsService,
} from "@/api";
import _ from "lodash";

type VacationState = {
  vacations: VacationListResponse;
  loading: boolean;
};

const state: VacationState = {
  vacations: [],
  loading: false,
};

const getters: GetterTree<VacationState, RootState> = {
  vacations: state => state.vacations,
  loading: state => state.loading,
};

const mutations: MutationTree<VacationState> = {
  SET_VACATIONS: (state, vacations: VacationListResponse) => {
    state.vacations = _.orderBy(vacations, ["date"], ["desc"]);
  },
  ADD_VACATIONS: (state, vacations: VacationListResponse) => {
    const combined = _.concat(state.vacations, vacations);
    state.vacations = _.uniqBy(_.orderBy(combined, ["date"], ["desc"]), "id");
  },
  UPDATE_VACATION: (state, vacation: Vacation) => {
    const index = state.vacations.findIndex(item => item.id === vacation.id);
    if (index > -1) {
      state.vacations.splice(index, 1, vacation);
    }
    state.vacations = _.orderBy(state.vacations, ["date"], ["desc"]);
  },
  DELETE_VACATION: (state, vacationId: number) => {
    state.vacations = state.vacations.filter(item => item.id !== vacationId);
  },
  SET_LOADING: (state, loading: boolean) => {
    state.loading = loading;
  },
};

const actions: ActionTree<VacationState, RootState> = {
  fetchVacations: async (
    { commit, rootState },
    { startDate, endDate }: { startDate: string; endDate: string },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    commit("SET_LOADING", true);
    try {
      const vacations = await VacationsService.getVacations({
        companyId,
        startDate,
        endDate,
      });
      if (vacations) {
        commit("SET_VACATIONS", vacations);
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    } finally {
      commit("SET_LOADING", false);
    }
  },
  addVacation: async ({ commit, rootState }, requestBody: VacationRequest) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const vacations = await VacationsService.addVacations({
        companyId,
        requestBody,
      });
      if (vacations) {
        commit("ADD_VACATIONS", vacations);
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateVacation: async (
    { commit, rootState },
    requestBody: VacationRequest,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const vacation = await VacationsService.updateVacations({
        companyId,
        vacationId: requestBody.id || 0,
        requestBody,
      });
      if (vacation) {
        commit("UPDATE_VACATION", vacation);
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  deleteVacation: async ({ commit, rootState }, vacationId: number) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      await VacationsService.deleteVacations({
        companyId,
        vacationId,
      });
      commit("DELETE_VACATION", vacationId);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
};

export const vacation: Module<VacationState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
